<template>
  <el-drawer
    title="Swap Details"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
  >
    <!-- head -->
    <section class="flex items-center justify-between gap-x-4 px-8">
      <div>
        <h2 class="text-lg lg:text-2xl">
          $ {{ addComma(swapDetails.depositedAmount.amount) }}
        </h2>
        <div class="flex items-center gap-x-2">
          <currency-icon
            :currency="swapDetails.fromAsset.toLowerCase()"
            size="h-4 w-4"
          />
          <span class="font-normal text-9px lg:text-sm"
            >{{ swapDetails.fromAsset }} ({{ swapDetails.fromNetwork }})</span
          >
        </div>
      </div>
      <swap-icon-2 />
      <div>
        <h2 class="text-lg lg:text-2xl">
          $ {{ addComma(swapDetails.transferredAmount.amount) }}
        </h2>
        <div class="flex items-center gap-x-2">
          <currency-icon
            :currency="swapDetails.toAsset.toLowerCase()"
            size="h-4 w-4"
          />
          <span class="font-normal text-9px lg:text-sm"
            >{{ swapDetails.toAsset }} ({{ swapDetails.toNetwork }})</span
          >
        </div>
      </div>
    </section>
    <!-- body -->
    <section class="px-8 mt-10">
      <ul class="font-light text-xs lg:text-sm">
        <li class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5">
          <span>Transaction ID</span>
          <span
            >{{ maskID(swapDetails.id) }}
            <span
              class="cursor-pointer mb-1 ml-2"
              v-clipboard:copy="swapDetails.id"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </span>
        </li>
        <li class="flex items-center border-b py-3.5 lg:py-5">
          <span class="w-1/2">Status</span>
          <span
            :class="statusClass2(swapDetails.status)"
            class="px-4 py-1 rounded-2xl text-center capitalize text-9px lg:text-xs"
            >{{ formatServerName(swapDetails.status) }}</span
          >
        </li>
        <li class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5">
          <span>Fees</span>
          <span
            class="px-1 py-1 rounded-2xl w-24 text-center capitalize font-medium"
            >{{ addComma(swapDetails.fee.amount) }}
            {{ swapDetails.fromAsset }}</span
          >
        </li>
        <!-- deposit details -->
        <li class="flex items-center justify-between border-b py-3.5 lg:py-5">
          <span>Deposit Details</span>
          <span
            class="text-primary font-semibold text-xl cursor-pointer"
            @click="toggleShow('showDepositDetails')"
            >{{ showDepositDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showDepositDetails">
            <li
              class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5 text-10px lg:text-xs"
            >
              <span>Wallet Tag</span>
              <span>{{ swapDetails.destination.tag }}</span>
            </li>
            <li
              class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5 text-10px lg:text-xs"
            >
              <span>Deposited Amount</span>
              <span
                >{{ addComma(swapDetails.depositedAmount.amount) }}
                {{ swapDetails.fromAsset }}</span
              >
            </li>
            <li
              class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5 text-10px lg:text-xs"
            >
              <span>Recipient Amount</span>
              <span>
                {{ addComma(swapDetails.transferredAmount.amount) }}
                {{ swapDetails.toAsset }}</span
              >
            </li>
          </div>
        </transition>
        <li class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5">
          <span>Destination Address</span>
          <span
            >{{ maskID(swapDetails.destination.walletAddress) }}
            <span
              class="cursor-pointer mb-1 ml-2"
              v-clipboard:copy="swapDetails.destination.walletAddress"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </span>
        </li>
        <li class="grid grid-cols-2 items-center border-b py-3.5 lg:py-5">
          <span>Time & Date</span>
          <span class=""
            >{{ $dayjs(dispTimeDate).format('DD MMM YYYY') }} |
            {{ parseLocalTime(dispTimeDate) }}</span
          >
        </li>

        <li
          class="flex justify-between items-center border-b py-3.5 lg:py-5 cursor-pointer"
          @click="toggleShow('showInitiatorDetails')"
          v-if="swapDetails.status === $STATUS.AWAITING_BUSINESS_APPROVAL"
        >
          <span>Initiator's Details</span>
          <span class="text-primary font-semibold text-xl">{{
            showInitiatorDetails ? '-' : '+'
          }}</span>
        </li>
        <transition name="slide-fade">
          <div v-if="showInitiatorDetails">
            <div class="pt-3">
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
                v-for="(item, index) in swapInitiatorDetails"
                :key="index"
              >
                <p class="text-sm font-thin">{{ item.label }}</p>
                <p class="text-sm font-normal text-black-10">
                  {{ item.value }}
                </p>
              </div>
            </div>
          </div>
        </transition>
      </ul>

      <!-- Buttons -->
      <div v-if="isAwaitingStatus" class="flex flex-col gap-3 mt-8">
        <el-tooltip
          content="You do not have the access to approve this swap"
          placement="top"
          :disabled="hasApproverAccess"
        >
          <button
            @click="processSwap('approve')"
            :disabled="!hasApproverAccess"
            class="bg-primary text-white"
          >
            Approve Swaps
          </button>
        </el-tooltip>

        <el-tooltip
          content="You do not have the access to cancel this swap"
          placement="top"
          :disabled="hasApproverAccess"
        >
          <button
            @click="processSwap('cancel')"
            :disabled="!hasApproverAccess"
            class="text-pink-3 border border-pink-3"
          >
            Cancel Swaps
          </button>
        </el-tooltip>
      </div>
    </section>
  </el-drawer>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import {
  MaskWalletAddressTwo,
  AddComma,
  ParseLocalTime
} from '@/utils/helpers.js'
import app from '@/mixins/app.js'
import userKinds from '@/mixins/userKinds.js'

export default {
  name: 'Drawer',
  mixins: [app, userKinds],
  data () {
    return {
      breakpoints,
      isLoading: false,
      showDepositDetails: false,
      showInitiatorDetails: false,
      search: ''
    }
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    },
    swapDetails: {
      type: Object,
      default: () => {},
      required: true
    }
  },
  components: {
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    SwapIcon2: () => import('@/assets/v2/icons/SwapIcon2.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue')
  },
  computed: {
    drawerWidth () {
      return breakpoints.lgAndUp ? '30%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    dispTimeDate () {
      return this.swapDetails.status === this.$STATUS.COMPLETED
        ? this.swapDetails.completedAt
        : this.swapDetails.createdAt
    },
    isAwaitingStatus () {
      return this.swapDetails.status === this.$STATUS.AWAITING_BUSINESS_APPROVAL
    },
    swapInitiatorDetails () {
      return [
        {
          label: 'Name',
          value: `${this.initiatorInfo.firstName || '-'}  ${
            this.initiatorInfo.lastName || '-'
          }`
        },
        {
          label: 'Email Address',
          value: this.initiatorInfo.email || '--'
        },
        {
          label: 'Time Initiated',
          value: this.swapDetails.createdAt
            ? this.$dayjs(this.swapDetails.createdAt).format(
                'DD MMM YYYY, h:mm A'
              )
            : '--'
        }
      ]
    }
  },
  methods: {
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    handleClose () {
      this.$emit('closeDrawer')
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    parseLocalTime (time) {
      return ParseLocalTime(time)
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    processSwap (type) {
      this.$emit('handleSwap', type)
      this.handleClose()
    }
  }
}
</script>
<style scoped>
>>> .el-drawer__header > :first-child {
  font-size: 0.9rem;
  color: var(--text-2);
  font-weight: 600;
}
>>> .el-drawer__header {
  color: var(--text-4);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-2);
}
>>> .el-drawer {
  background: var(--background-9) !important;
}
>>> .el-input--prefix .el-input__inner {
  border: 1px solid var(--border-1);
  border-radius: 8px;
  margin: 0, 6px;
  font-size: 0.9rem;
  color: var(--text-2);
  font-weight: 400;
  padding-left: 40px;
  background: var(--background-10);
}
.small-text {
  font-size: 0.6rem;
}

>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: var(--background-2) !important;
}
>>> li.number.active {
  background-color: var(--background) !important;
  border: 0.5px solid var(--color-primary) !important;
  box-sizing: border-box;
  border-radius: 100px;
  font-weight: 400;
  color: var(--color-primary) !important ;
}
>>> li.number {
  background-color: var(--background) !important;
  border: 0.5px solid var(--text-1) !important;
  box-sizing: border-box;
  border-radius: 100px !important;
  font-weight: 400;
  /* color: var(--color-primary) !important ; */
}
button {
  padding: 17px 0;
  font-size: 14px;
  border-radius: 8px;

  &:hover {
    opacity: 0.8;
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.2;
    transform: translateY(0);
  }
}
</style>
